import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';
import * as yup from 'yup';

import { createUrl, request } from 'app/utils';
import { Flex, Icons, Loading, Page, Typography } from 'components';
import Alert, { useAlert } from 'features/alert';
import { CenteredLayout, useEducational } from 'features/educational';
import { EducationalInstitutionTryActivateCodeResponse } from 'features/educational/types';
import { useSchool } from 'features/school';
import { AxiosError } from 'axios';

const Input = styled.input`
  border: 2px solid ${({ theme }) => theme.colors.gray_100};
  border-radius: 4px;
  padding: 18px;
  font-size: 20px;
  text-align: center;
  letter-spacing: 3px;
  outline: none;
  color: ${({ theme }) => theme.colors.gray_600};
  font-weight: 500;
  width: 95%;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray_300};
  }
`;

const Button = styled.button`
  border: 0;
  border-radius: 4px;
  padding: 15px 20px;
  font-size: 18px;
  text-align: center;
  outline: none;
  font-weight: 500;
  background: ${({ theme }) => theme.colors.primary_400};
  color: white;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary_200};
  }
`;

const Icon = styled(Icons)`
  width: 20px;
  color: ${({ theme }) => theme.colors.gray_400};
`;

const schema = yup
  .object()
  .shape({
    activationCode: yup.string().required('Você precisa informar o código de ativação'),
  })
  .required();

const Main = () => {
  const { colors } = useTheme();

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const { schoolUuid } = useSchool();
  const { setActivateCode } = useEducational();
  const { setAlert, clearAlert } = useAlert();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<{ activationCode: string }>({
    resolver: yupResolver(schema),
    defaultValues: {
      activationCode: '',
    },
  });

  //TODO: limpar login do usuário
  //TODO: adicionar timezone no perfil do usuário (my account)

  const onSubmit = useCallback(
    async (data) => {
      clearAlert();
      setLoading(true);

      const { activationCode } = data;

      await request<EducationalInstitutionTryActivateCodeResponse>({
        url: '/api/v1/School/EducationalInstitution/TryActivateCode',
        method: 'POST',
        data: {
          schoolUuid,
          activationCode: activationCode,
        },
      })
        .then(({ data }) => {
          setActivateCode(data, activationCode);

          navigate(createUrl('educational/register/switcher'));
        })
        .catch((error: AxiosError<{ errorMessage: string }>) => {
          let message = 'O código informado não existe ou já foi utilizado.';

          if (error.response?.data?.errorMessage) {
            message = error.response.data.errorMessage;
          }

          setAlert({ type: 'error', message });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [schoolUuid]
  );

  return (
    <>
      <Page title="MELVER nas Escolas">
        {loading && <Loading alpha />}

        <CenteredLayout>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Flex flexDirection="column" alignItems="center" style={{ gap: 20 }}>
              <Flex flexDirection="column" alignItems="center" textAlign="center" style={{ gap: 5 }}>
                <Typography type="type5" fontWeight="bold">
                  Digite seu código de registro
                </Typography>

                <Typography type="type3">
                  O código está colado no verso do seu material didático.
                  <br />
                  Ele é composto por letras e números.
                </Typography>
              </Flex>

              <Flex width={1} justifyContent={'center'}>
                <Input
                  placeholder="RD-ESC-TURM-00000000"
                  {...register('activationCode')}
                  style={errors?.activationCode && { borderColor: colors.alert_400, borderWidth: 2 }}
                  autoFocus
                />
              </Flex>

              <Flex style={{ gap: 5 }}>
                <Icon icon="alertCircle" />
                <Typography type="type0" color="gray_400">
                  A Melver nunca solicita códigos por telefone ou aplicativos de mensagem.
                  <br />
                  Não compartilhe seu código com outras pessoas.
                </Typography>
              </Flex>

              <Alert invert px={0} py={0} style={{ fontWeight: 'bold' }} />

              <Button type="submit" disabled={isSubmitting}>
                Confirmar código
              </Button>

              {/* <Link to="/educational/help">
                <Typography type="type1" color="primary_400" underline>
                  Precisa de ajuda?
                </Typography>
              </Link> */}
            </Flex>
          </form>

          <Link to={createUrl('login')}>
            <Typography type="type1" underline>
              Eu já tenho login
            </Typography>
          </Link>
        </CenteredLayout>
      </Page>
    </>
  );
};

export default Main;
